/* eslint-disable react-refresh/only-export-components -- bewusst co-lozierter Provider+Hook bzw. Helfer-Export; react-refresh ist eine reine Dev-HMR-Optimierung, kein Prod-/Qualitaetsthema. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme-Verwaltung für Light / Dark / System-Auto.
 *
 * - `system`: folgt der OS-Einstellung (prefers-color-scheme).
 * - `light` / `dark`: User-Override, persistiert in localStorage.
 *
 * Setzt die Tailwind-Klasse `dark` auf <html>. Komponenten verwenden
 * `dark:`-Modifier in Klassen, statt eigene Style-Switches zu führen.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ignisai.theme';

interface ThemeContextValue {
  mode: ThemeMode;
  effective: 'light' | 'dark';
  setMode: (m: ThemeMode) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemPref(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyClass(effective: 'light' | 'dark'): void {
  const root = document.documentElement;
  if (effective === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // Sync mobile-browser chrome (address bar) mit User-Override.
  // index.html hat zwei media-basierte Fallbacks; dieser JS-Sync gewinnt
  // sobald React läuft und respektiert den expliziten User-Switch.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  const color = effective === 'dark' ? '#000E22' : '#F5F7FA';
  if (meta) meta.content = color;
  else {
    const m = document.createElement('meta');
    m.name = 'theme-color';
    m.content = color;
    document.head.appendChild(m);
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
  });

  const [systemPref, setSystemPref] = useState<'light' | 'dark'>(() => getSystemPref());

  // System-Änderungen mithören
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystemPref(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const effective: 'light' | 'dark' = mode === 'system' ? systemPref : mode;

  useEffect(() => {
    applyClass(effective);
  }, [effective]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode etc. */
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(effective === 'dark' ? 'light' : 'dark');
  }, [effective, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, effective, setMode, toggle }),
    [mode, effective, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
